import {
  ApiReturnAutomaticSensor,
  ApiReturnManualSensor,
} from '@highimpact-frontend/api-types';
import assert from 'assert';
import { forwardRef } from 'react';
import StateDecorator from '../../../../../src/components/utils/state/StateDecorator';
import { DataState, isDataState } from '../../../../../src/store/data/state';
import { useAppSelector } from '../../../../../src/store/hooks';
import ManagementDecorator from '../../ManagementDecorator';
import AutomaticSensors from './AutomaticSensors';
import ManualSensors from './ManualSensors';
import { Typography } from '@mui/material';
import DevicesTabs from './DevicesTabs';

const DevicesList = forwardRef<HTMLDivElement, React.PropsWithChildren<object>>(
  ({ children }, ref) => {
    const projects = useAppSelector(
      (state) => state.data_slice.externals.projects
    );
    const project_sites = useAppSelector(
      (state) => state.data_slice.externals.project_sites
    );
    const sites = useAppSelector((state) => state.data_slice.externals.sites);
    const thematics = useAppSelector(
      (state) => state.data_slice.externals.thematics
    );
    const devices = useAppSelector(
      (state) => state.data_slice.externals.devices
    );
    const device_sites = useAppSelector(
      (state) => state.data_slice.externals.site_devices
    );
    const measure_types = useAppSelector(
      (state) => state.data_slice.externals.measure_types
    );
    const device_type_measure_types = useAppSelector(
      (state) => state.data_slice.externals.device_type_measure_types
    );
    const measure_points= useAppSelector(
      (state) => state.data_slice.externals.measure_points
    );
    const device_types = useAppSelector(
      (state) => state.data_slice.externals.device_types
    );

    return ManagementDecorator(ref, () =>
      StateDecorator(
        [
          projects,
          project_sites,
          sites,
          thematics,
          devices,
          device_sites,
          device_types,
          device_type_measure_types,
          measure_types,
          measure_points,
          device_type_measure_types,
        ],
        ref,
        () => {
          assert(isDataState(projects));
          assert(isDataState(project_sites));
          assert(isDataState(sites));
          assert(isDataState(thematics));
          assert(isDataState(devices));
          assert(isDataState(device_sites));
          assert(isDataState(device_types));
          assert(isDataState(measure_types));
          assert(isDataState(measure_points));
          assert(isDataState(device_type_measure_types));

          const manualSensors = Object.values(devices.data).filter(
            (dv) => dv.type === 'manual_sensor'
          ) as ApiReturnManualSensor[];

          const automaticSensors = Object.values(devices.data).filter(
            (dv) => dv.type === 'automatic_sensor'
          ) as ApiReturnAutomaticSensor[];

          const manualDeviceTypes = Object.values(device_types.data).filter(
            (dv) => dv.type === 'manual_sensor'
          );

          const automaticDeviceTypes = Object.values(device_types.data).filter(
            (dv) => dv.type === 'automatic_sensor'
          );

          // Filter active stations (no end_assignement_date)
          const activeStations: DataState<string, typeof measure_points.data> = {
            ...measure_points,
            data: Object.fromEntries(
              Object.entries(measure_points.data).filter(([, site]) => {
                const endDate = new Date(site.device_site?.end_assignement_date!).getTime();
                const nowDate = Date.now();
                const isActive =
                  endDate === null || endDate === 0 || endDate > nowDate;
                const isMeteoFrance =
                  site.name?.includes('Météo France');
                return isActive && !isMeteoFrance;
              })
            ),
          };
          if (manualSensors.length !== 0 && manualDeviceTypes.length === 0) return <Typography>Verifier les types de mesures des dispositifs de mesure</Typography>
          if (automaticSensors.length !== 0 && automaticDeviceTypes.length === 0) return <Typography>Verifier les types de mesures des capteur connectés</Typography>
          const ManualSensor = (
            <ManualSensors
              manualSensors={manualSensors}
              thematics={Object.values(thematics.data)}
              projects={Object.values(projects.data)}
              project_sites={Object.values(project_sites.data)}
              sites={sites}
              device_sites={device_sites}
              measure_points={Object.values(measure_points.data)}
              deviceTypes={manualDeviceTypes}
              measureTypes={measure_types}
              deviceTypeMeasureTypes={device_type_measure_types}
            />
          );
          const AutomaticSensor = (
            <AutomaticSensors
              automaticSensors={automaticSensors}
              thematics={Object.values(thematics.data)}
              projects={Object.values(projects.data)}
              project_sites={Object.values(project_sites.data)}
              sites={sites}
              device_sites={device_sites}
              measure_points={Object.values(measure_points.data)}
              measureTypes={measure_types}
              deviceTypes={automaticDeviceTypes}
              deviceTypeMeasureTypes={device_type_measure_types}
            />
          );

          return (            
            <DevicesTabs
              manualSensor={ManualSensor}
              automaticSensor={AutomaticSensor}
            />
          );
        }
      )
    );
  }
);

export default DevicesList;
